import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import Vapi from "@vapi-ai/web";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import loaderContext from "../../../../context/loader/LoaderContext";
import FinalReportSubscriber from "../LLMReportHeader/FinalReportSubscriber";
import { t } from 'i18next';
import AgenticReportingContext from '../../../../context/agentic-reporting/AgenticReportingContext';
import MobileViewerContext from '../../../../context/studyviewer/MobileViewerContext';
import { isMobile } from 'react-device-detect';
import { getEnv } from '../../../../helpers/EnvHelper';

const VAPI_PUBLIC_KEY = getEnv("REACT_APP_VAPI_PUBLIC_KEY");
const VAPI_GENERATE_ASSISTANT_ID = getEnv("REACT_APP_VAPI_GENERATE_ASSISTANT_ID");
const VAPI_UPDATE_ASSISTANT_ID = getEnv("REACT_APP_VAPI_UPDATE_ASSISTANT_ID");
const REPORT_FUNCTION_NAME = getEnv("REACT_APP_VAPI_TOOL_NAME") || 'GENERATE-REPORT';

const VoiceInputControl = () => {
  const { ref, setLoaderRunning } = useContext(loaderContext);
  const mobileViewerCtx = useContext(MobileViewerContext);
  const setSlideReportOpen = isMobile ? mobileViewerCtx?.setSlideReportOpen : null;
  const {
    setAlertSuccessModal,
    alertSuccessModal,
  } = useContext(studyReportContext);

  const {
    setVapiFunctions,
    isFirstCall,
    setVoiceAssistantSummary,
    setCurrentVapiTranscript,
    setStenographState,
    getVapiConfigPayload,
    handleSuccessfulReport,
    handleVoiceReportGeneration,
    setIsVapiConnecting,
    setIsVapiStenographActive,
    setIsTalking
  } = useContext(AgenticReportingContext);

  const [isListeningForReport, setIsListeningForReport] = useState(false);
  const timeoutRef = useRef(null);

  const [assistantSpeaking, setAssistantSpeaking] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const vapiRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationIdRef = useRef(null);
  const isManualEndRef = useRef(false);

  useEffect(() => {
    if (isListeningForReport) {
      timeoutRef.current = setTimeout(() => {
        setLoaderRunning(false);
        if (ref.current) ref.current.complete();
        setIsListeningForReport(false);
        setAlertSuccessModal({
          show: true,
          heading: t("llm-reporting.report-generation-in-progress"),
          description: t("llm-reporting.timeout-modal-description"),
          onOk: () => setAlertSuccessModal({ ...alertSuccessModal, show: false }),
          okText: "Got it",
        });
      }, 60000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isListeningForReport, setLoaderRunning, ref, alertSuccessModal, setAlertSuccessModal]);

  const handleReportReceived = useCallback((finalReport, voiceAssistantSummary, status, message) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (status === 'failure') {
      console.error("Report generation failed:", message);
      setLoaderRunning(false);
      if (ref.current) ref.current.complete();
      setIsListeningForReport(false);

      setAlertSuccessModal({
        show: true,
        heading: "Error",
        description: t("llm-reporting.timeout-modal-description"),
        onOk: () => setAlertSuccessModal({ ...alertSuccessModal, show: false }),
        okText: "OK",
      });
      return;
    }
    handleSuccessfulReport(finalReport, null);

    if (voiceAssistantSummary) {
      setVoiceAssistantSummary(voiceAssistantSummary);
    }
    setLoaderRunning(false);
    if (ref.current) ref.current.complete();
    setIsListeningForReport(false);
  }, [handleSuccessfulReport, setVoiceAssistantSummary, setLoaderRunning, ref]);

  const handleCallEnd = useCallback(async () => {
    console.log("endeddddddd");
  }, []);

  const startMicAnalysis = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioCtx();

      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.fftSize || 2048);
      const analyze = () => {
        analyserRef.current.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const isSpeaking = rms > 0.02;
        const amplitude = Math.min(rms * 8, 1);

        // setStenographState(prevState => ({
        //   ...prevState,
        //   userSpeaking: isSpeaking,
        //   amplitude: amplitude,
        // }));
        window.dispatchEvent(new CustomEvent('vapi-audio-level', {
          detail: { amplitude, userSpeaking: isSpeaking }
        }));
        animationIdRef.current = requestAnimationFrame(analyze);
      };
      analyze();
    } catch (err) { console.error("Mic access error:", err); }
  };

  const stopMicAnalysis = useCallback(() => {
    if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
    if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(() => { });
    }
    // setStenographState(prevState => ({
    //   ...prevState,
    //   userSpeaking: false,
    //   amplitude: 0,
    // }));
    window.dispatchEvent(new CustomEvent('vapi-audio-level', {
      detail: { amplitude: 0, userSpeaking: false }
    }));
  }, []);

  const stopAssistant = useCallback((isManual = false) => {
    isManualEndRef.current = isManual;
    vapiRef.current?.stop();

    if (isManual) {
      setIsVapiStenographActive(false);
      setIsVapiConnecting(false);
      setStenographState(prev => ({ ...prev, userSpeaking: false, assistantSpeaking: false, amplitude: 0 }));
      setIsTalking(false);
      setCurrentVapiTranscript("");
      stopMicAnalysis();
    }
  }, [setIsVapiStenographActive, setIsVapiConnecting, setStenographState, setIsTalking, setCurrentVapiTranscript, stopMicAnalysis]);

  const startAssistant = useCallback(async () => {
    isManualEndRef.current = false;
    setIsConnecting(true);
    setIsTalking(true);
    setIsVapiStenographActive(true);
    setIsVapiConnecting(true);
    try {
      const payload = await getVapiConfigPayload?.();

      const assistantIdToCall = isFirstCall
        ? VAPI_GENERATE_ASSISTANT_ID
        : VAPI_UPDATE_ASSISTANT_ID;

      console.log(`Starting Vapi call. isFirstCall: ${isFirstCall}`);
      await vapiRef.current.start(assistantIdToCall, {
        variableValues: payload || {},
        recordingEnabled: false,
      });
      setIsConnecting(false);
    } catch (err) {
      console.error("Error starting assistant:", err);
      stopAssistant(true);
      setIsVapiConnecting(false);
    }
  }, [getVapiConfigPayload, isFirstCall, stopAssistant, setIsTalking, setIsVapiStenographActive, setIsVapiConnecting]);

  const toggleMute = useCallback(() => {
    if (vapiRef.current) {
      setStenographState(prev => {
        const newMuted = !prev.isMuted;
        vapiRef.current.setMuted(newMuted);
        return { ...prev, isMuted: newMuted };
      });
    }
  }, [setStenographState]);

  const cancelVapiTranscript = useCallback(() => stopAssistant(true), [stopAssistant]);
  const confirmVapiTranscript = useCallback(() => stopAssistant(true), [stopAssistant]);

  useEffect(() => {
    if (!vapiRef.current) vapiRef.current = new Vapi(VAPI_PUBLIC_KEY);
    const vapi = vapiRef.current;

    const handleFunctionCall = async (message) => {
      if (
        message.type === 'model-output' &&
        Array.isArray(message.output) &&
        message.output.length > 0
      ) {
        const toolCall = message.output[0];
        if (toolCall.type === 'function' && toolCall.function) {
          if (toolCall.function.name === REPORT_FUNCTION_NAME) {
            console.log("Tool call : ", REPORT_FUNCTION_NAME, " detected.");
            console.log("Raw arguments:", toolCall.function.arguments);

            setTimeout(async () => {
              stopAssistant(false);
              setIsVapiStenographActive(false);
              setIsTalking(false);
              setAssistantSpeaking(false);
              setLoaderRunning(true);
              if (setSlideReportOpen) setSlideReportOpen(true);

              if (ref.current) ref.current.continuousStart();

              let args = {};
              try {
                args = JSON.parse(toolCall.function.arguments);
              } catch (e) {
                console.error("Failed to parse Vapi arguments", e);
              }

              if (handleVoiceReportGeneration) {
                setIsListeningForReport(true);

                const success = await handleVoiceReportGeneration(args);

                if (!success) {
                  console.log("Report generation failed. Stopping listener.");
                  setLoaderRunning(false);
                  if (ref.current) ref.current.complete();
                  setIsListeningForReport(false);
                }
              }
            }, 1000);
          }
        }
      }
    };

    const handleStart = () => {
      if (isManualEndRef.current) {
        vapiRef.current?.stop();
        return;
      }
      setIsVapiConnecting(false);
      setStenographState({ userSpeaking: false, assistantSpeaking: false, amplitude: 0, isMuted: false });
      setIsVapiStenographActive(true);
      setIsTalking(true);
      setCurrentVapiTranscript("");
      startMicAnalysis();
    };

    const handleEnd = () => {
      if (!isManualEndRef.current) {
        setIsVapiStenographActive(false);
        setIsVapiConnecting(false);
        setStenographState({ userSpeaking: false, assistantSpeaking: false, amplitude: 0 });
        setIsTalking(false);
        setCurrentVapiTranscript("");
        stopMicAnalysis();
        handleCallEnd();
        if (setSlideReportOpen) setSlideReportOpen(true);
      }
      isManualEndRef.current = false;
    };

    const handleSpeechStart = () => {
      setAssistantSpeaking(true);
      setStenographState(prevState => ({ ...prevState, assistantSpeaking: true }));
    };
    const handleSpeechEnd = () => {
      setAssistantSpeaking(false);
      setStenographState(prevState => ({ ...prevState, assistantSpeaking: false }));
    };
    const handleTranscript = (transcript) => {
      if (transcript.type === 'partial' || transcript.type === 'final') {
        setCurrentVapiTranscript(transcript.transcript);
      }
    };
    const handleError = (e) => {
      console.error("Vapi Error:", e);
      setIsVapiStenographActive(false);
      setIsTalking(false);
      stopMicAnalysis();
    };

    vapi.on("call-start", handleStart);
    vapi.on("call-end", handleEnd);
    vapi.on("speech-start", handleSpeechStart);
    vapi.on("speech-end", handleSpeechEnd);
    vapi.on('message', handleFunctionCall);
    vapi.on('transcript', handleTranscript);
    vapi.on('error', handleError);

    return () => {
      vapi.off("call-start", handleStart);
      vapi.off("call-end", handleEnd);
      vapi.off("speech-start", handleSpeechStart);
      vapi.off("speech-end", handleSpeechEnd);
      vapi.off('message', handleFunctionCall);
      vapi.off('transcript', handleTranscript);
      vapi.off('error', handleError);
    };
  }, [handleCallEnd, stopAssistant, stopMicAnalysis, setIsVapiStenographActive, setIsTalking, setCurrentVapiTranscript, setStenographState, handleVoiceReportGeneration]);

  const latestFuncsRef = useRef({
    startAssistant,
    stopAssistant,
    confirmVapiTranscript,
    cancelVapiTranscript,
    toggleMute
  });

  latestFuncsRef.current = {
    startAssistant,
    stopAssistant,
    confirmVapiTranscript,
    cancelVapiTranscript,
    toggleMute
  };

  useEffect(() => {
    if (setVapiFunctions) {
      setVapiFunctions({
        startAssistant: (...args) => latestFuncsRef.current.startAssistant(...args),
        stopAssistant: (...args) => latestFuncsRef.current.stopAssistant(...args),
        confirmVapiTranscript: (...args) => latestFuncsRef.current.confirmVapiTranscript(...args),
        cancelVapiTranscript: (...args) => latestFuncsRef.current.cancelVapiTranscript(...args),
        toggleMute: (...args) => latestFuncsRef.current.toggleMute(...args),
      });
    }
  }, [setVapiFunctions]);

  return (
    <FinalReportSubscriber
      onReportReceived={handleReportReceived}
      isListening={isListeningForReport}
    />
  );
};

export default VoiceInputControl;